import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { useDispatch, useSelector } from 'react-redux';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import { uploadFile } from "../../../core/data/redux/awsBucketSlice";
import { uploadPhoto, inclusionOfDependent, deletionOfDependent } from '../../../core/data/redux/idCardSlice';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { toast } from 'react-toastify';
import moment from "moment";

const DependentManagementForm = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const employeesState = useSelector((state: RootState) => state.employeesState);
    const user = employeesState?.data?.data;

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [uploadedFile, setUploadedFile] = React.useState<string | null>(null);
    const [formData, setFormData] = React.useState({
        requestType: "",
        monthlyIncome: "",
        additionOf: "",
        singleChild: "",
        medicalBenefit: "",
        dmrcWorker: "",
        remarks: ""
    });

    React.useEffect(() => {
        dispatch(getMyProfile({}));
    }, [dispatch]);

    const employeeData = {
        empId: user?.details?.empId || '---',
        name: `${user?.details?.firstname || ""} ${user?.details?.lsastname || ""}`.trim() || '---',
        designation: user?.details?.sap_department_designation_wings?.designation_name || '---',
        payScale: user?.details?.pay_scale || '---',
        basicPay: user?.details?.basic_pay || '---',
        contactNo: user?.details?.phone || '---'
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64String = reader.result as string;
            const rawBase64 = base64String.split(',')[1];

            const response = await dispatch(uploadFile({
                filestring: rawBase64,
                mime_type: file.type || "application/octet-stream",
                module: "id-cards/documents"
            }));

            if (uploadFile.fulfilled.match(response)) {
                const fileUrl = response.payload.data?.url || response.payload.url;
                setUploadedFile(fileUrl);
                toast.success("Document uploaded successfully");
            } else {
                toast.error("Failed to upload document");
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.requestType) {
            toast.warning("Please select request type");
            return;
        }

        setIsSubmitting(true);
        const payload = {
            ...formData,
            document_url: uploadedFile,
            employee_id: user?.details?.empId
        };

        let response;
        if (formData.requestType === 'inclusion') {
            response = await dispatch(inclusionOfDependent(payload));
        } else {
            response = await dispatch(deletionOfDependent(payload));
        }

        setIsSubmitting(false);

        if (inclusionOfDependent.fulfilled.match(response) || deletionOfDependent.fulfilled.match(response)) {
            toast.success("Request submitted successfully");
            navigate(routes.idCard.inclusionOfDependent);
        } else {
            toast.error("Failed to submit request");
        }
    };

    return (
        <div className="page-wrapper" style={{ backgroundColor: '#f4f7fb', minHeight: '100vh' }}>
            <div className="content">
                {/* Header Section with Breadcrumbs - matching NOC style */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <div className="d-flex align-items-center">
                            <Link to={routes.idCard.inclusionOfDependent} className="text-primary text-decoration-none me-3">
                                <i className="ti ti-arrow-left fs-24"></i>
                            </Link>
                            <h3 className="page-title mb-0">
                                Application for Inclusion & Deletion (Dependent)
                            </h3>
                        </div>
                        <nav className="mt-2">
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={routes.services.list}>Services</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={routes.idCard.list}>ID Card Management</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    Inclusion & Deletion
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row justify-content-center">
                        <div className="col-12">

                            {/* Instructions Alert - matching NOC style */}
                            <div className="mb-4 p-3 bg-info bg-opacity-10 border-start border-info border-4">
                                <h6 className="fw-bold text-info flex-grow-1 mb-2">
                                    📄 Supporting Documents Guidelines:
                                </h6>
                                <ul className="mb-0 ps-3 text-dark small">
                                    <li><strong>Mandatory:</strong> ID proof (Aadhaar, Voter ID, etc.) and Residence proof are mandatory.</li>
                                    <li><strong>Parents:</strong> Income proof (PPO, ITR, or Certificate) is required for parents.</li>
                                    <li><strong>Retired Parents:</strong> Certificate of no post-retirement medical benefits is mandatory.</li>
                                </ul>
                            </div>

                            {/* Section 1: Employee Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Employee Information</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Employee No.</label>
                                            <input type="text" className="form-control" style={{ backgroundColor: '#f8f9fa' }} value={employeeData.empId} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Employee Name</label>
                                            <input type="text" className="form-control" style={{ backgroundColor: '#f8f9fa' }} value={employeeData.name} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Designation</label>
                                            <input type="text" className="form-control" style={{ backgroundColor: '#f8f9fa' }} value={employeeData.designation} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Pay Scale / Basic Pay</label>
                                            <input type="text" className="form-control" style={{ backgroundColor: '#f8f9fa' }} value={`${employeeData.payScale} / ${employeeData.basicPay}`} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Contact No. <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" defaultValue={employeeData.contactNo} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Request Details */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Request Details</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Request Type <span className="text-danger">*</span></label>
                                            <select className="form-select" name="requestType" value={formData.requestType} onChange={handleInputChange}>
                                                <option value="">Select Option</option>
                                                <option value="inclusion">Inclusion</option>
                                                <option value="deletion">Deletion</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Monthly Income of Parent (₹) <span className="text-danger">*</span></label>
                                            <input type="number" className="form-control" name="monthlyIncome" value={formData.monthlyIncome} onChange={handleInputChange} placeholder="Enter income (Enter 1 if NIL)" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label text-dark fw-medium">Addition/Deletion Of <span className="text-danger">*</span></label>
                                            <select className="form-select" name="additionOf" value={formData.additionOf} onChange={handleInputChange}>
                                                <option value="">Select Relation</option>
                                                <option value="father">Father</option>
                                                <option value="mother">Mother</option>
                                                <option value="spouse">Spouse</option>
                                                <option value="son">Son</option>
                                                <option value="daughter">Daughter</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Eligibility & Verification */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Eligibility Verification</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-12 mb-3 pb-3 border-bottom">
                                            <label className="form-label text-dark fw-medium">Whether employee is a single child <span className="text-danger">*</span></label>
                                            <div className="d-flex gap-4 mt-1">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="singleChild" value="yes" checked={formData.singleChild === 'yes'} onChange={handleInputChange} id="childYes" />
                                                    <label className="form-check-label text-dark" htmlFor="childYes">Yes</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="singleChild" value="no" checked={formData.singleChild === 'no'} onChange={handleInputChange} id="childNo" />
                                                    <label className="form-check-label text-dark" htmlFor="childNo">No</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-3 pb-3 border-bottom">
                                            <label className="form-label text-dark fw-medium">Post retirement medical benefit being availed by parents <span className="text-danger">*</span></label>
                                            <div className="d-flex flex-wrap gap-4 mt-1">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="medicalBenefit" value="yes" checked={formData.medicalBenefit === 'yes'} onChange={handleInputChange} id="benefitYes" />
                                                    <label className="form-check-label text-dark" htmlFor="benefitYes">Yes</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="medicalBenefit" value="no" checked={formData.medicalBenefit === 'no'} onChange={handleInputChange} id="benefitNo" />
                                                    <label className="form-check-label text-dark" htmlFor="benefitNo">No</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="medicalBenefit" value="na" checked={formData.medicalBenefit === 'na'} onChange={handleInputChange} id="benefitNA" />
                                                    <label className="form-check-label text-dark" htmlFor="benefitNA">Not Applicable</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-2">
                                            <label className="form-label text-dark fw-medium">Whether sibling or spouse of employee working in DMRC <span className="text-danger">*</span></label>
                                            <div className="d-flex gap-4 mt-1">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="dmrcWorker" value="yes" checked={formData.dmrcWorker === 'yes'} onChange={handleInputChange} id="workerYes" />
                                                    <label className="form-check-label text-dark" htmlFor="workerYes">Yes</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="dmrcWorker" value="no" checked={formData.dmrcWorker === 'no'} onChange={handleInputChange} id="workerNo" />
                                                    <label className="form-check-label text-dark" htmlFor="workerNo">No</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Supporting Documents */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Supporting Documents</h5>
                                </div>
                                <div className="card-body">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-dark fw-medium mb-3">Required Documents (PDF/JPG ONLY) <span className="text-danger">*</span></label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input type="file" id="fileUpload" className="d-none" accept=".pdf,.jpg,.jpeg" onChange={handleFileChange} />
                                            <button type="button" className="btn btn-outline-primary d-flex align-items-center text-decoration-none" onClick={() => document.getElementById('fileUpload')?.click()}>
                                                <i className="ti ti-file-upload me-1"></i>
                                                Choose File
                                            </button>
                                            {uploadedFile && (
                                                <span className="text-success small">
                                                    <i className="ti ti-check-circle me-1"></i>
                                                    Document Selected
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: Remarks & Action */}
                            <div className="card mb-5">
                                <div className="card-body p-4">
                                    <div className="mb-4">
                                        <label className="form-label text-dark fw-medium">Remarks</label>
                                        <textarea className="form-control" name="remarks" value={formData.remarks} onChange={handleInputChange} rows={3} placeholder="Enter your remarks if any..."></textarea>
                                    </div>
                                    <div className="d-flex justify-content-center gap-3">
                                        <button type="button" className="btn btn-outline-secondary px-5" style={{ minWidth: '180px' }} onClick={() => navigate(-1)}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary px-5" style={{ minWidth: '180px' }} disabled={isSubmitting}>
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Submitting...
                                                </>
                                            ) : 'Submit Request'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DependentManagementForm;
